 
import { LocationHour } from '@/entities/depots/models/Depot'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { DynamicFieldData } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { DynamicFormDefinition } from '@/shared/components/DynamicForm/models/DynamicFormSection'
import { DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LON } from '@/shared/config/consts'
import { FormFieldDefaultValue, FormFieldSelectOptions, getDynamicFormFields } from '@/shared/lib/getDynamicFormFields'
import { timeHhMmSsToDayjs } from '@/shared/lib/timeHelper'
import { pxToRem } from '@/shared/styles/themes'
import { Box } from '@mui/material'

// Default General Details
const defaultGeneralDetailFields: DynamicFieldData[] = [
  {
    fieldType: 'section',
    fieldName: 'generalInfoSectionTitle',
    label: 'General Information',
    showDivider: false,
  },
  {
    fieldType: 'select',
    fieldName: 'groupId',
    label: 'Group',
    placeholder: 'Select Group',
    required: true,
    options: [],
    config: {
      required: 'Group is required',
    },
  },
  {
    fieldType: 'text',
    fieldName: 'externalId',
    label: 'Code',
    placeholder: 'Enter Depot Code',
    defaultValue: '',
    required: true,
    config: {
      required: 'Code is required',
    },
  },
  {
    fieldType: 'text',
    fieldName: 'name',
    label: 'Name',
    placeholder: 'Enter Depot Name',
    defaultValue: '',
    required: true,
    config: {
      required: 'Name is required',
    },
  },
  {
    fieldType: 'select',
    fieldName: 'uiColor',
    label: 'UI Color',
    placeholder: 'Select UI Color',
    required: true,
    options: [],
    config: {
      required: 'UI Color is required',
    },
    renderOption: (props, option) => {
      const { ...optionProps } = props

      return (
        <Box
          component="li"
          {...optionProps}
        >
          <Box
            sx={{
              width: pxToRem(12),
              height: pxToRem(12),
              borderRadius: pxToRem(2),
              marginRight: pxToRem(8),
              backgroundColor: (option as DropdownOption).value,
            }}
          />
          {(option as DropdownOption).label}
        </Box>
      )
    },
  },
]

// Default Address Fields
const defaultAddressFields: DynamicFieldData[] = [
  {
    fieldType: 'section',
    fieldName: 'addressInformationSectionTitle',
    label: 'Address',
    showDivider: false,
  },
  {
    fieldType: 'address',
    fieldName: 'address',
    label: 'Address',
    defaultValue: {
      street: '',
      suburb: '',
      state: '',
      postcode: '',
      country: 'Australia',
    },
  },
]

// Default Opening Hours Fields
type WeekDay = {
  name: string
  key: string
  label: string
  weekday: number
}

const WEEKDAYS: WeekDay[] = [
  { name: 'Monday', key: 'monday', label: 'Monday', weekday: 1 },
  { name: 'Tuesday', key: 'tuesday', label: 'Tuesday', weekday: 2 },
  { name: 'Wednesday', key: 'wednesday', label: 'Wednesday', weekday: 3 },
  { name: 'Thursday', key: 'thursday', label: 'Thursday', weekday: 4 },
  { name: 'Friday', key: 'friday', label: 'Friday', weekday: 5 },
  { name: 'Saturday', key: 'saturday', label: 'Saturday', weekday: 6 },
  { name: 'Sunday', key: 'sunday', label: 'Sunday', weekday: 0 },
]

const generateInitialOpeningHours = () => {
  return WEEKDAYS.map(day => ({
    id: 0,
    locationId: 0,
    weekday: day.weekday,
    openingHoursStart: '00:00',
    openingHoursEnd: '00:00',
    openingHoursEndNextDay: false,
    tw1Start: '00:00',
    tw1StartNextDay: false,
    tw1End: '00:00',
    tw1EndNextDay: false,
    tw2Start: '00:00',
    tw2StartNextDay: false,
    tw2End: '00:00',
    tw2EndNextDay: false,
  } as LocationHour))
}

const defaultOpeningHoursFields: DynamicFieldData[] = [{
  fieldType: 'openingHours',
  fieldName: 'locationHours',
  label: 'Opening Hours',
  defaultValue: generateInitialOpeningHours(),
}]

// Default Advanced Fields
const defaultAdvancedFields: DynamicFieldData[] = [
  {
    fieldType: 'section',
    fieldName: 'advancedSection',
    label: 'Advance Settings',
    showDivider: false,
  },
  {
    fieldType: 'time',
    fieldName: 'siteTime',
    label: 'Site Time',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
  },
  {
    fieldType: 'time',
    fieldName: 'loadTime',
    label: 'Load Time',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
  },
  {
    fieldType: 'time',
    fieldName: 'unloadTime',
    label: 'Unload Time',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
  },
  {
    fieldType: 'number',
    fieldName: 'loadingBays',
    label: 'Loading Bays',
    defaultValue: 0,
    config: {
      min: {
        value: 0,
        message: 'Loading bays cannot be less than 0',
      },
    },
  },
  {
    fieldType: 'time',
    fieldName: 'loadingBayGap',
    label: 'Loading Bay Gap',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
  },
]

// Initial Depot Form Values
export const initialDepotFormValues = {
  groupId: undefined,
  externalId: '',
  name: '',
  uiColor: undefined,
  street: '',
  suburb: '',
  postcode: '',
  state: '',
  country: '',
  x: DEFAULT_MAP_CENTER_LON,
  y: DEFAULT_MAP_CENTER_LAT,
  siteTime: timeHhMmSsToDayjs('00:00'),
  loadTime: timeHhMmSsToDayjs('00:00'),
  unloadTime: timeHhMmSsToDayjs('00:00'),
  loadingBays: 0,
  loadingBayGap: timeHhMmSsToDayjs('00:00'),
  isActive: true,
  locationHours: generateInitialOpeningHours(),
}

type GetDepotFormDefinitionArgs = {
  defaultValues?: FormFieldDefaultValue
  selectOptions?: FormFieldSelectOptions
}

export const getDepotFormDefinition = ({
  defaultValues,
  selectOptions,
}: GetDepotFormDefinitionArgs): DynamicFormDefinition => {
  const generalFields = getDynamicFormFields({
    fields: defaultGeneralDetailFields,
    defaultValues,
    selectOptions,
  })

  const addressFields = getDynamicFormFields({
    fields: defaultAddressFields,
    defaultValues,
    selectOptions,
  })

  const openingHoursFields = getDynamicFormFields({
    fields: defaultOpeningHoursFields,
    defaultValues,
    selectOptions,
  })

  const advancedFields = getDynamicFormFields({
    fields: defaultAdvancedFields,
    defaultValues,
    selectOptions,
  })

  const formDefinition: DynamicFormDefinition = {
    formType: 'tabs',
    sections: [
      {
        label: 'General',
        fields: generalFields,
      },
      {
        label: 'Address',
        fields: addressFields,
      },
      {
        label: 'Opening Hours',
        fields: openingHoursFields,
      },
      {
        label: 'Advanced',
        fields: advancedFields,
      },
    ],
  }

  return formDefinition
}